// =============================================
// pdfRotate — turn pages of a stored PDF (Process Docs viewer → Rotate)
// =============================================
// Scans often arrive sideways or upside down. Rotation is written INTO the PDF (the
// page's /Rotate entry, via pdf-lib), so every viewer, the download and the auto-read
// see the page the right way up. The page content itself is not re-rendered, so
// nothing is lost however often a page is turned.
// =============================================
import { PDFDocument, degrees } from 'pdf-lib';

export type RotateBy = 90 | -90 | 180;

/** True when the bytes are a PDF (starts with "%PDF", allowing a little junk before it). */
export function isPdfBytes(bytes: Uint8Array | null | undefined): boolean {
  if (!bytes || bytes.length < 5) return false;
  const end = Math.min(1024, bytes.length) - 4;
  for (let i = 0; i <= end; i++) {
    // "%PDF" = 0x25 0x50 0x44 0x46
    if (bytes[i] === 0x25 && bytes[i + 1] === 0x50 && bytes[i + 2] === 0x44 && bytes[i + 3] === 0x46) return true;
  }
  return false;
}

/** Keep an angle in 0 / 90 / 180 / 270. */
export function normaliseAngle(angle: number): number {
  return ((Math.round(angle / 90) * 90) % 360 + 360) % 360;
}

/**
 * Rotate the given pages (1-based) — or every page — by `by` degrees clockwise
 * (negative = anticlockwise), on top of any rotation they already have.
 * Returns the new PDF bytes; the input is not changed.
 */
export async function rotatePdfPages(
  bytes: Uint8Array,
  pages: number[] | 'all',
  by: RotateBy,
): Promise<Uint8Array> {
  const doc = await PDFDocument.load(bytes, { ignoreEncryption: true, updateMetadata: false });
  const all = doc.getPages();
  const targets = pages === 'all'
    ? all.map((_, i) => i)
    : pages.map(p => p - 1).filter(i => i >= 0 && i < all.length);
  if (targets.length === 0) throw new Error('No such page in this document');
  for (const i of targets) {
    const page = all[i];
    page.setRotation(degrees(normaliseAngle(page.getRotation().angle + by)));
  }
  return doc.save();
}
